import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import AppSidebar from "~/components/AppSidebar";
import AuthLoader from "~/components/AuthLoader";
import PitchWatcher from "~/components/agencies/PitchWatcher";
import PastDueBanner from "~/components/billing/PastDueBanner";
import ViewerBanner from "~/components/ViewerBanner";
import NavigationHeader from "~/components/NavigationHeader";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import { Toaster } from "~/components/ui/sonner";
import { useAuth } from "~/context/AuthContext";
import { SubscriptionProvider } from "~/context/SubscriptionContext";
import { useNavigate } from "~/lib/team-router";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

function ChatLayoutContent({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [sidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar:state");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    localStorage.setItem("sidebar:state", sidebarOpen.toString());
  }, [sidebarOpen]);

  if (!user) {
    return null;
  }

  return (
    <SidebarProvider
      className="h-screen"
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 72)",
          "--header-height": "calc(var(--spacing) * 12)",
        } as React.CSSProperties
      }
    >
      <AppSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        conversations={conversations}
        setConversations={setConversations}
      />
      <SidebarInset className="flex flex-col min-h-0">
        <NavigationHeader conversations={conversations} />
        <PastDueBanner />
        <ViewerBanner />
        <PitchWatcher />

        <main className="flex flex-1 flex-col min-h-0">
          <div className="@container/main flex flex-1 flex-col gap-2 min-h-0">
            {children}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default function ChatLayout() {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <AuthLoader />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SubscriptionProvider>
      <Toaster richColors position="top-center" />
      <ChatLayoutContent>
        <Outlet />
      </ChatLayoutContent>
    </SubscriptionProvider>
  );
}
